import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Dictation, type DictationProblem, speechRecognizer } from '../lib/dictation';

export type DictationApi = {
  /** The browser can recognise speech at all (false in Firefox and some in-app browsers). */
  supported: boolean;
  listening: boolean;
  /** Words heard but not final yet — show them, don't insert them. */
  interim: string;
  problem: DictationProblem | null;
  start: () => void;
  /** Stops listening; words still being recognised are passed to `onText` first (synchronously). */
  stop: () => void;
};

/**
 * Voice typing for one text box: `onText` receives each final phrase (unformatted — see `insertDictation`). Listening
 * restarts by itself when the browser ends a session on a pause, and stops when the page is hidden or the box unmounts.
 */
export function useDictation(onText: (text: string) => void, lang: string): DictationApi {
  const latest = useRef(onText);
  useLayoutEffect(() => {
    latest.current = onText;
  });

  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [problem, setProblem] = useState<DictationProblem | null>(null);
  const [dictation] = useState(() => {
    const Recognizer = speechRecognizer();
    if (!Recognizer) return null;
    return new Dictation(Recognizer, { onText: (text) => latest.current(text), onInterim: setInterim, onListening: setListening, onProblem: setProblem }, lang);
  });

  useEffect(() => dictation?.setLang(lang), [dictation, lang]);

  useEffect(() => {
    if (!dictation) return;
    // A phone locking or switching apps must not leave the microphone on.
    const onVisibility = () => {
      if (document.hidden) dictation.stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      dictation.dispose();
      setListening(false);
      setInterim('');
    };
  }, [dictation]);

  return {
    supported: dictation !== null,
    listening,
    interim,
    problem: dictation ? problem : 'unsupported',
    start: () => dictation?.start(),
    stop: () => dictation?.stop(),
  };
}
